import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';

// Payment requests queue (2026-09-27). Lazarus drafts; a person sends. Nothing on this page contacts a client
// except the Send button, and Send checks the ledger first (a request already covered by a payment or a manual
// receipt is marked paid instead of going out).
const money = (c) => `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const when = (s) => (s ? new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export default function PaymentRequests() {
  const [requests, setRequests] = useState([]);
  const [terms, setTerms] = useState([]);
  const [cases, setCases] = useState({});
  const [policy, setPolicy] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('queue');

  const load = useCallback(async () => {
    try {
      const [r, t, p] = await Promise.all([api.get('/payment-requests'), api.get('/fee-terms'), api.get('/fee-terms/policy')]);
      setRequests(r.data.requests || []); setTerms(t.data.terms || []); setPolicy(p.data || null);
      const ids = [...new Set([...(r.data.requests || []), ...(t.data.terms || [])].map((x) => x.case_id).filter(Boolean))];
      const map = {};
      await Promise.all(ids.map(async (id) => { try { const c = await api.get(`/cases/${id}`); map[id] = c.data; } catch (e) { map[id] = null; } }));
      setCases(map);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const label = (caseId) => { const c = cases[caseId]; return c ? `${c.display_number || ''} ${c.client_name || c.case_name || ''}`.trim() : (caseId ? caseId.slice(0, 8) : 'no matter'); };
  const act = async (key, fn, okText) => {
    setBusy(key); setMsg(''); setErr('');
    try { const res = await fn(); setMsg(okText(res)); await load(); }
    catch (e) { setErr(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : (e.message || 'failed')); }
    finally { setBusy(''); }
  };

  const drafts = requests.filter((r) => r.status === 'draft');
  const open = requests.filter((r) => r.status === 'open');
  const awaiting = requests.filter((r) => r.status === 'awaiting_signature');
  const proposed = terms.filter((t) => t.status === 'proposed');
  const active = terms.filter((t) => t.status === 'active');

  const Card = ({ children }) => <div className="bg-slate-800/40 border border-cyan-500/20 rounded-2xl p-4 mb-3">{children}</div>;
  const Btn = ({ k, onClick, children, danger }) => (
    <button disabled={busy === k} onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-semibold mr-2 ${danger ? 'bg-rose-600/80 hover:bg-rose-600' : 'bg-cyan-600/80 hover:bg-cyan-600'} text-white disabled:opacity-40`}>
      {busy === k ? '…' : children}
    </button>
  );
  const Req = ({ r }) => (
    <Card>
      <div className="flex justify-between gap-4 flex-wrap">
        <div>
          <div className="text-cyan-100 font-semibold">{label(r.case_id)} <span className="text-cyan-400 font-normal">· {r.reason}</span></div>
          <div className="text-sm text-cyan-300/80 mt-1">{money(r.amount_cents)}{r.surcharge_cents ? ` + ${money(r.surcharge_cents)} surcharge` : ''} · {r.policy} · {r.rail}{r.sent_at ? ` · sent ${when(r.sent_at)}` : ''}{r.paid_at ? ` · paid ${when(r.paid_at)}` : ''}</div>
          {r.url && <a className="text-xs text-cyan-400 underline" href={r.url} target="_blank" rel="noreferrer">payment link</a>}
        </div>
        <div className="flex items-start">
          {(r.status === 'draft' || r.status === 'open') && <Btn k={`send-${r.id}`} onClick={() => act(`send-${r.id}`, () => api.post(`/payment-requests/${r.id}/send`, {}), (res) => res.data.sent ? `Sent to ${res.data.to} (${res.data.amount})` : `Not sent: ${res.data.reason}`)}>{r.status === 'draft' ? 'Send' : 'Resend'}</Btn>}
          {r.status !== 'paid' && <Btn danger k={`cancel-${r.id}`} onClick={() => act(`cancel-${r.id}`, () => api.post(`/payment-requests/${r.id}/cancel`), () => 'Cancelled')}>Cancel</Btn>}
        </div>
      </div>
    </Card>
  );
  const Term = ({ t }) => (
    <Card>
      <div className="flex justify-between gap-4 flex-wrap">
        <div className="flex-1">
          <div className="text-cyan-100 font-semibold">{label(t.case_id)} <span className="text-cyan-400 font-normal">· {t.fee_type}{t.total_cents ? ` ${money(t.total_cents)}` : ''}{t.hourly_rate_cents ? ` · ${money(t.hourly_rate_cents)}/h` : ''}{t.initial_cents ? ` · initial ${money(t.initial_cents)}` : ''} · {t.policy} · {t.payer_type}</span></div>
          {t.evidence && <div className="text-xs text-cyan-300/70 mt-1">{t.evidence}</div>}
          {t.phases.length > 0 && <div className="mt-2 text-sm">
            {t.phases.map((p) => <div key={p.id} className="flex items-center gap-2 text-cyan-200/90">
              <span>{p.seq}. {p.name} — {money(p.amount_cents)} · {p.trigger_kind}{p.trigger_event ? ` (${p.trigger_event})` : ''} · <b>{p.status}</b></span>
              {p.status === 'pending' && t.status === 'active' && <Btn k={`trig-${p.id}`} onClick={() => act(`trig-${p.id}`, () => api.post(`/fee-terms/phases/${p.id}/trigger`), () => 'Triggered — drafts on the next run')}>Trigger</Btn>}
            </div>)}
          </div>}
          {t.installments.length > 0 && <div className="mt-1 text-xs text-cyan-300/70">{t.installments.filter((i) => i.status === 'scheduled').length} scheduled installments · next {t.installments.find((i) => i.status === 'scheduled')?.due_on || '—'} · {money(t.installments.filter((i) => i.status === 'scheduled').reduce((a, i) => a + i.amount_cents, 0))} remaining</div>}
        </div>
        <div className="flex items-start">
          {t.status === 'proposed' && <>
            <Btn k={`ok-${t.id}`} onClick={() => act(`ok-${t.id}`, () => api.post(`/fee-terms/${t.id}/confirm`), () => 'Confirmed')}>Confirm</Btn>
            <Btn danger k={`no-${t.id}`} onClick={() => act(`no-${t.id}`, () => api.post(`/fee-terms/${t.id}/reject`), () => 'Rejected')}>Reject</Btn>
          </>}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen relative">
      <SpaceBackground />
      <div className="relative max-w-5xl mx-auto p-6">
        <div className="flex justify-between items-baseline mb-4">
          <h1 className="text-2xl font-bold text-white">Payment requests</h1>
          <Link to="/invoices" className="text-sm text-cyan-400">Invoices →</Link>
        </div>
        {policy && <div className={`rounded-xl px-4 py-2 mb-4 text-sm ${policy.policy === 'bank_only' ? 'bg-amber-900/40 border border-amber-500/40 text-amber-200' : 'bg-emerald-900/40 border border-emerald-500/40 text-emerald-200'}`}>
          Policy: <b>{policy.policy}</b>{policy.bank_only_threshold_cents ? ` · card below ${money(policy.bank_only_threshold_cents)}` : ''} · surcharge {policy.surcharge_pct}% — {policy.reason}
        </div>}
        {msg && <div className="text-emerald-300 text-sm mb-2">{msg}</div>}
        {err && <div className="text-rose-300 text-sm mb-2">{err}</div>}
        <div className="flex gap-2 mb-4">
          {[['queue', `Drafts (${drafts.length})`], ['open', `Sent, unpaid (${open.length + awaiting.length})`], ['proposed', `Proposed terms (${proposed.length})`], ['terms', `Terms (${active.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-lg text-sm ${tab === k ? 'bg-cyan-600 text-white' : 'bg-slate-800/60 text-cyan-300'}`}>{l}</button>
          ))}
          <button onClick={() => act('draft', () => api.post('/payment-requests/draft-due'), (res) => `Drafted ${res.data.drafted.length}`)} className="ml-auto px-3 py-1.5 rounded-lg text-sm bg-slate-800/60 text-cyan-300 border border-cyan-500/30">Draft what's due now</button>
        </div>
        {tab === 'queue' && (drafts.length ? drafts.map((r) => <Req key={r.id} r={r} />) : <div className="text-cyan-300/70 text-sm">Nothing drafted. Installments due and triggered phases show up here every morning.</div>)}
        {tab === 'open' && (open.length + awaiting.length ? [...open, ...awaiting].map((r) => <Req key={r.id} r={r} />) : <div className="text-cyan-300/70 text-sm">No open requests.</div>)}
        {tab === 'proposed' && (proposed.length ? proposed.map((t) => <Term key={t.id} t={t} />) : <div className="text-cyan-300/70 text-sm">No proposals waiting.</div>)}
        {tab === 'terms' && active.map((t) => <Term key={t.id} t={t} />)}
      </div>
    </div>
  );
}
